<template>
  <div ref="chart" class="echarts-store-rank-chart" role="img" :aria-label="ariaLabel" />
</template>

<script>
import echarts, { chartAnimationDuration } from './echarts-core'

const COLORS = ['#0d2d2c', '#5a78ff', '#c18b45', '#8b5f82', '#667a3d']
const DARK_COLORS = ['#4ADE80', '#8ea2ff', '#e0b476', '#c797bb', '#a4b875']

export default {
  name: 'EChartsStoreRankChart',
  props: {
    periods: { type: Array, required: true },
    stores: { type: Array, required: true },
    ariaLabel: { type: String, default: 'Store revenue rank over time' },
    isDark: { type: Boolean, default: false }
  },
  data() {
    return { chart: null, resizeObserver: null }
  },
  mounted() {
    this.$nextTick(this.initChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.refresh)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.chart) this.chart.dispose()
  },
  watch: {
    periods: { handler() { this.updateChart() }, deep: true },
    stores: { handler() { this.updateChart() }, deep: true },
    isDark() { this.updateChart() }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart, null, { renderer: 'canvas' })
      window.addEventListener('resize', this.refresh)
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
      this.updateChart()
    },
    refresh() {
      this.$nextTick(() => this.chart && this.chart.resize())
    },
    rankAxisMaximum(stores) {
      return Math.max(2, ...stores.flatMap(store =>
        (store.points || []).map(point => Number(point.rank) || 0)
      ))
    },
    updateChart() {
      if (!this.chart) return this.$nextTick(this.initChart)
      const textColor = this.isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)'
      const gridColor = this.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
      const colors = this.isDark ? DARK_COLORS : COLORS
      const visibleStores = this.stores.slice(0, 5)
      const maxRank = this.rankAxisMaximum(visibleStores)
      this.chart.setOption({
        animationDuration: chartAnimationDuration(),
        color: colors,
        grid: { top: 48, right: 26, bottom: 34, left: 20, containLabel: true },
        legend: {
          top: 0,
          type: 'scroll',
          data: visibleStores.map(store => store.warehouseName),
          textStyle: { color: textColor, fontSize: 10 }
        },
        tooltip: {
          trigger: 'axis',
          renderMode: 'richText',
          confine: true,
          backgroundColor: this.isDark ? 'rgba(35,35,35,0.96)' : 'rgba(20,20,20,0.88)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: params => {
            const points = Array.isArray(params) ? params.filter(point => point.data?.value != null) : []
            if (!points.length) return ''
            return [points[0].axisValue, ...points.map(point => {
              const revenue = Number(point.data.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              return `${point.marker}${point.seriesName}: #${point.data.value} · $${revenue}`
            })].join('\n')
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.periods.map(period => period.label),
          axisLabel: { color: textColor, hideOverlap: true, margin: 12 },
          axisLine: { lineStyle: { color: gridColor } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          inverse: true,
          min: 1,
          max: maxRank,
          minInterval: 1,
          name: 'Revenue rank',
          nameTextStyle: { color: textColor, fontSize: 11 },
          axisLabel: { color: textColor, formatter: value => `#${value}` },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: gridColor } }
        },
        series: visibleStores.map((store, index) => ({
          name: store.warehouseName,
          type: 'line',
          connectNulls: false,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 2.25, color: colors[index] },
          itemStyle: { color: colors[index], borderColor: this.isDark ? '#232323' : '#fff', borderWidth: 1.5 },
          data: store.points.map(point => ({ value: point.rank, revenue: point.revenue, invoices: point.invoices }))
        }))
      }, true)
      this.refresh()
    }
  }
}
</script>

<style scoped>
.echarts-store-rank-chart { width: 100%; height: 100%; min-height: 300px; }
</style>
